import { validateBoardDef } from '../board/board';
import type { BoardDef } from '../board/types';
import { validateRoster } from '../characters/roster';
import type { CharacterDef } from '../characters/types';
import { isDuelLevel, type DuelLevel } from '../battle/aiSkill';
import { validateArenaDef, validateTilesetDef } from '../battle/arenaDef';
import type { ArenaDef, TilesetDef } from '../battle/types';
import type { CardRole, GameState } from './types';
import { signatureStands, type ArenaCalibration } from './arenaCalibration';
import { ELEMENTS, elementalId } from './elementals';
import { SPELL_COSTS } from './spellCosts';

export const COMBAT_MODES = ['arena', 'card', 'roll', 'watch'] as const;
export type CombatMode = (typeof COMBAT_MODES)[number];
export const ENGAGEMENT_RULES = ['immediate', 'muster'] as const;
export type EngagementRule = (typeof ENGAGEMENT_RULES)[number];

/** A character model the client ships: a GLB under its public `/characters/` folder. */
const MODEL_PATH = /^\/characters\/[a-zA-Z0-9/_-]+\.glb$/;

export const CARD_FITS = [
  'melee',
  'wounded',
  'ranged',
  'venom',
  'frost',
  'spellcaster',
  'sturdy',
  'arcane',
  'grounded',
  'mimic',
  'fire',
] as const;
export type CardFit = (typeof CARD_FITS)[number];

/** Immutable once a match starts. All editable content travels with the game/save/replay. */
export interface GameDefinition {
  version: 1;
  id: string;
  name: string;
  board: BoardDef;
  characters: CharacterDef[];
  arenas: ArenaDef[];
  tilesets: TilesetDef[];
  defaultArenaId: string;
  /** Missing entries explicitly inherit the immutable Default Arena. */
  cellArenas: Record<string, string>;
  players: { min: number; max: number };
  /** Modes the author permits. Older games allow all four and resolve captures immediately. */
  combatRules?: {
    modes: CombatMode[];
    defaultMode: CombatMode;
    engagements: EngagementRule[];
    defaultEngagement: EngagementRule;
  };
  /** Authored support identity per regular character; omitted entries use a trait fallback. */
  cardSupports?: Record<string, { role: CardRole; fit: CardFit }>;
  /** Measured Expert Arena matchups, ignored automatically after relevant editor changes. */
  arenaCalibration?: ArenaCalibration[];
  /** Legacy main dominance scale: higher values reduce support leverage (0.5 is standard). */
  cardScoreScale?: number;
  /**
   * The Roll curve by level: a fight between two seats at one level is settled on
   * `Φ(c·Φ⁻¹(p))` of its odds p, so a curve under one makes the favourite's edge smaller and the
   * draw more open (`rollCurve` in `rollBattle.ts`). One, the odds exactly, where a level is not
   * named. Roll's own settlement setting (policy v7, section 5), never a character's.
   */
  rollCurve?: Partial<Record<DuelLevel, number>>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Validates an untrusted game (bundled, imported, published or sent to the server). Every refusal
 * names what it is about and the value it found (content:quality#8): "Invalid game: characters"
 * told an author nothing. The prefix stays, for callers that tell validation from other errors.
 */
export function validateGameDefinition(input: unknown): GameDefinition {
  const fail = (message: string): never => {
    throw new Error(`Invalid game: ${message}`);
  };
  // Each part is looked at before it is read, so a null in a list is refused, not a TypeError
  // (content:bugs#4, content:quality#11).
  if (!isRecord(input)) fail('a game must be an object');
  const g = input as unknown as GameDefinition;
  if (g.version !== 1) fail(`version ${JSON.stringify(g.version)} is not 1`);
  if (typeof g.id !== 'string' || !/^[a-z0-9][a-z0-9-]{0,63}$/.test(g.id))
    fail(`id ${JSON.stringify(g.id)} must be kebab-case, at most 64 characters`);
  if (typeof g.name !== 'string' || !g.name.trim() || g.name.length > 100)
    fail('name must be 1–100 characters');
  if (JSON.stringify(g).length > 180000) fail('maximum size is 180 KB');
  const board = validateBoardDef(g.board);
  if (board.id !== g.id) fail(`board id '${board.id}' must match game id '${g.id}'`);
  if (!Array.isArray(g.characters) || g.characters.length < 1 || g.characters.length > 40)
    fail(
      `characters: between 1 and 40, found ${Array.isArray(g.characters) ? g.characters.length : 'none'}`,
    );
  // The spells, their points and the attack's recovery are the roster's own checks now, so a
  // bundled roster is held to them too (core-rules:quality#10, content:quality#10).
  const roster = validateRoster(g.characters);
  for (const c of g.characters) {
    const who = `character '${c.id}'`;
    if (c.force !== undefined && c.force > 1000)
      fail(`${who}: strength points ${c.force} exceed 1000`);
    if (
      !Number.isFinite(c.hitPoints) ||
      c.hitPoints > 1000 ||
      !Number.isFinite(c.attack.damage) ||
      c.attack.damage > 1000
    )
      fail(
        `${who}: character statistics out of range (hit points ${c.hitPoints} and damage ${c.attack.damage}, at most 1000)`,
      );
    if (
      c.model &&
      (!MODEL_PATH.test(c.model.file) ||
        (c.model.boardFile !== undefined && !MODEL_PATH.test(c.model.boardFile)) ||
        !Number.isFinite(c.model.boardHeight ?? 0.09) ||
        (c.model.boardHeight ?? 0.09) > 0.5)
    )
      fail(`${who}: unsupported character model '${c.model.file}'`);
  }
  for (const p of board.placements)
    if (!roster[p.character] || roster[p.character]!.summoned)
      fail(`starting character '${p.character}' is not in the roster, or is summoned`);
  if (
    !isRecord(g.players) ||
    !Number.isInteger(g.players.min) ||
    !Number.isInteger(g.players.max) ||
    g.players.min < 2 ||
    g.players.max > board.factions.length ||
    g.players.min > g.players.max
  )
    fail(
      `player limits ${JSON.stringify(g.players)}: whole numbers, at least 2 and at most the board's ${board.factions.length} sides`,
    );
  if (g.combatRules) {
    const r = g.combatRules;
    if (
      !Array.isArray(r.modes) ||
      r.modes.length < 1 ||
      r.modes.some((m) => !COMBAT_MODES.includes(m)) ||
      new Set(r.modes).size !== r.modes.length ||
      !r.modes.includes(r.defaultMode)
    )
      fail(
        `combat modes ${JSON.stringify(r.modes)} with default ${JSON.stringify(r.defaultMode)}: each of ${COMBAT_MODES.join(', ')} at most once, the default among them`,
      );
    if (
      !Array.isArray(r.engagements) ||
      r.engagements.length < 1 ||
      r.engagements.some((e) => !ENGAGEMENT_RULES.includes(e)) ||
      new Set(r.engagements).size !== r.engagements.length ||
      !r.engagements.includes(r.defaultEngagement)
    )
      fail(
        `engagement rules ${JSON.stringify(r.engagements)} with default ${JSON.stringify(r.defaultEngagement)}: each of ${ENGAGEMENT_RULES.join(', ')} at most once, the default among them`,
      );
    if (
      r.defaultEngagement === 'muster' &&
      (r.defaultMode === 'arena' || r.defaultMode === 'watch')
    )
      fail('Arena and Watch require immediate engagement');
  }
  if (g.cardSupports) {
    if (!isRecord(g.cardSupports)) fail('card supports must be an object');
    for (const [id, support] of Object.entries(g.cardSupports)) {
      if (!roster[id] || roster[id]!.summoned)
        fail(`card support character '${id}' is not in the roster, or is summoned`);
      if (!isRecord(support)) fail(`card support '${id}' must be an object`);
      if (!['pressure', 'protection', 'deception'].includes(support.role))
        fail(`card support role ${JSON.stringify(support.role)} of '${id}'`);
      if (!CARD_FITS.includes(support.fit))
        fail(`card support fit ${JSON.stringify(support.fit)} of '${id}'`);
    }
  }
  if (!Array.isArray(g.tilesets) || g.tilesets.length < 1 || g.tilesets.length > 4)
    fail(
      `tilesets: between 1 and 4, found ${Array.isArray(g.tilesets) ? g.tilesets.length : 'none'}`,
    );
  // The core's own checks first, then the game's tighter bounds on what they return.
  const tilesets = g.tilesets.map((t) => validateTilesetDef(t));
  const sets = Object.fromEntries(tilesets.map((t) => [t.id, t]));
  if (Object.keys(sets).length !== tilesets.length) fail('duplicate tileset');
  for (const t of tilesets) {
    if (t.tiles.length > 32) fail(`tileset '${t.id}' has ${t.tiles.length} tiles, at most 32`);
    const tall = t.tiles.find((b) => b.height > 8);
    if (tall)
      fail(`tile bounds: tileset '${t.id}' tile '${tall.id}' height ${tall.height} exceeds 8`);
  }
  if (!Array.isArray(g.arenas) || g.arenas.length < 1 || g.arenas.length > 12)
    fail(`arenas: between 1 and 12, found ${Array.isArray(g.arenas) ? g.arenas.length : 'none'}`);
  const ids = new Set<string>();
  for (const raw of g.arenas) {
    const a = validateArenaDef(raw, sets);
    if (a.width > 32 || a.height > 32)
      fail(`arena '${a.id}' is ${a.width}×${a.height}; arena dimensions exceed 32`);
    if (ids.has(a.id)) fail(`duplicate arena '${a.id}'`);
    ids.add(a.id);
  }
  if (!ids.has(g.defaultArenaId)) fail(`missing default arena ${JSON.stringify(g.defaultArenaId)}`);
  if (!isRecord(g.cellArenas)) fail('cell arenas must be an object');
  for (const [key, arena] of Object.entries(g.cellArenas)) {
    if (!/^\d+,\d+$/.test(key)) fail(`cell coordinates '${key}' must be x,y`);
    const [x, y] = key.split(',').map(Number) as [number, number];
    if (!ids.has(arena))
      fail(
        `arena assignment '${key}' names arena ${JSON.stringify(arena)}, which is not in the game`,
      );
    if (!board.cells[y]?.[x] || board.cells[y]![x] === '#')
      fail(`arena assignment '${key}' points outside the board or at a void cell`);
  }
  if (
    g.cardScoreScale !== undefined &&
    (!Number.isFinite(g.cardScoreScale) || g.cardScoreScale < 0.25 || g.cardScoreScale > 4)
  )
    fail('card score scale');
  if (
    g.rollCurve !== undefined &&
    (!isRecord(g.rollCurve) ||
      Object.entries(g.rollCurve).some(
        ([level, c]) => !isDuelLevel(level) || !Number.isFinite(c) || c! < 0.25 || c! > 2,
      ))
  )
    fail('roll curve');
  if (g.arenaCalibration !== undefined) {
    if (!Array.isArray(g.arenaCalibration) || g.arenaCalibration.length > 12)
      fail('arena calibration');
    const tableKeys = new Set<string>();
    for (const entry of g.arenaCalibration) {
      if (
        !entry ||
        entry.version !== 1 ||
        typeof entry.arena !== 'string' ||
        !Number.isInteger(entry.samples) ||
        entry.samples < 1
      )
        fail('calibration version/samples');
      // One table per arena and level; a level's table carries no reference (only Expert's
      // evidence anchors the estimate for an edited character).
      if (
        (entry.level !== undefined && !isDuelLevel(entry.level)) ||
        (entry.level !== undefined && entry.level !== 'expert' && entry.reference !== undefined) ||
        tableKeys.has(`${entry.arena}|${entry.level ?? 'expert'}`)
      )
        fail('calibration level');
      tableKeys.add(`${entry.arena}|${entry.level ?? 'expert'}`);
      if (
        !entry.characters ||
        typeof entry.characters !== 'object' ||
        Object.keys(entry.characters).length > 40 ||
        Object.values(entry.characters).some((x) => typeof x !== 'string')
      )
        fail('calibration characters');
      if (entry.reference !== undefined) {
        if (
          !entry.reference ||
          Array.isArray(entry.reference) ||
          typeof entry.reference !== 'object' ||
          Object.keys(entry.reference).length > 40
        )
          fail('calibration reference');
        const refs = validateRoster(Object.values(entry.reference));
        for (const [id, c] of Object.entries(refs)) {
          if (
            entry.reference[id] !== c ||
            !Number.isFinite(c.hitPoints) ||
            c.hitPoints > 1000 ||
            !Number.isFinite(c.attack.damage) ||
            c.attack.damage > 1000 ||
            !signatureStands(c, entry.characters[id], true)
          )
            fail('calibration reference signature');
        }
      }
      if (
        !entry.probabilities ||
        typeof entry.probabilities !== 'object' ||
        Object.keys(entry.probabilities).length > 1600 ||
        Object.entries(entry.probabilities).some(
          ([key, p]) =>
            !/^[a-z0-9-]+\|[a-z0-9-]+$/.test(key) || !Number.isFinite(p) || p < 0 || p > 1,
        )
      )
        fail('calibration probabilities');
      if (entry.wounded !== undefined) {
        const w = entry.wounded;
        if (
          !w ||
          typeof w !== 'object' ||
          !Array.isArray(w.shares) ||
          w.shares.length < 1 ||
          w.shares.length > 8 ||
          w.shares.some(
            (s, i) => !Number.isFinite(s) || s <= 0 || s >= 1 || (i > 0 && s <= w.shares[i - 1]!),
          ) ||
          !Number.isInteger(w.samples) ||
          w.samples < 1 ||
          badWoundedOdds(w.probabilities, w.shares.length) ||
          (w.defender !== undefined && badWoundedOdds(w.defender, w.shares.length))
        )
          fail('calibration wounded odds');
      }
    }
  }
  return g;
}

/** Wounded odds by ordered pair (`attacker>defender`), one probability per measured share. */
function badWoundedOdds(odds: unknown, shares: number): boolean {
  return (
    !odds ||
    typeof odds !== 'object' ||
    Object.keys(odds).length > 1600 ||
    Object.entries(odds).some(
      ([key, ps]) =>
        !/^[a-z0-9-]+>[a-z0-9-]+$/.test(key) ||
        !Array.isArray(ps) ||
        ps.length !== shares ||
        ps.some((p) => !Number.isFinite(p) || p < 0 || p > 1),
    )
  );
}

export function arenaForGame(game: GameDefinition, state?: Pick<GameState, 'pendingBattle'>) {
  const at = state?.pendingBattle;
  const id = (at && game.cellArenas[`${at.x},${at.y}`]) || game.defaultArenaId;
  const arena = game.arenas.find((a) => a.id === id)!;
  return { arena, tileset: game.tilesets.find((t) => t.id === arena.tileset)! };
}

const SPELL_NAMES: Readonly<Record<string, string>> = {
  'summon-elemental': 'Summon Elemental',
  'shift-time': 'Shift Time',
};
const spellName = (spell: string) =>
  SPELL_NAMES[spell] ?? spell.charAt(0).toUpperCase() + spell.slice(1);

/**
 * What an author is told about a game that plays but does not do what it says: a spell a caster
 * can never afford, Summon Elemental with no elementals in the roster to bring, an arena with no
 * name (content:quality#6, content:quality#12). Advice only: games saved or published with any of
 * these still load and play as before.
 */
export function definitionAdvice(def: GameDefinition): string[] {
  const advice: string[] = [];
  const roster = new Map(def.characters.map((c) => [c.id, c]));
  for (const c of def.characters) {
    const points = c.spellPoints ?? 0;
    for (const spell of c.spells ?? []) {
      const cost = SPELL_COSTS[spell];
      if (cost > points)
        advice.push(
          `${c.name} knows ${spellName(spell)}, which costs ${cost} spell points; with ${points} it can never cast it.`,
        );
    }
  }
  if (def.characters.some((c) => c.spells?.includes('summon-elemental'))) {
    const missing = ELEMENTS.filter((e) => !roster.get(elementalId(e))?.summoned);
    if (missing.length > 0)
      advice.push(
        `Summon Elemental brings the ${missing.join(', ')} elemental${missing.length > 1 ? 's' : ''}, which the roster lacks: in their phases it cannot be cast.`,
      );
  }
  if (def.arenas.some((a) => typeof a.name !== 'string' || !a.name.trim()))
    advice.push('Every arena needs a name: a blank one shows as an empty line in the arena lists.');
  return advice;
}
